import React, { useEffect, useState } from "react";
import { PROJECT_LOGO } from "constants/constants";
import _branding from "modules/branding";
import _authService from "modules/AuthService";

// Sprint 2.4B (Enterprise Experience Modernization): PublicLayout previously rendered no header at
// all — every pre-login screen (Login for all 5 portals, Registration, Forgot Password, T&C pages)
// had zero shared chrome/branding. This is the first real consumer of Sprint 2.4's branding module
// (modules/branding.js), which was built but never wired to anything — falls back to the existing
// PROJECT_LOGO/env-config value if branding hasn't loaded yet, so there is no regression if the
// anonymous branding fetch is slow or unavailable.
//
// Sprint 2.6.7A: re-fires the same idempotent applyBranding() call on mount and re-renders once it
// resolves — a genuine bug fix (§6 of 2.6.7B/C: logo/name must not depend on a transient localStorage
// timing issue), kept through every homepage sprint since.
//
// Sprint 2.6.7C (Port-85 Structural Alignment, §6): the port-85 reference implementation's own
// NewLogin.jsx renders the tenant logo inside the Candidate Access sidebar, not in a separate header
// bar above the page — this component's bar is a newer addition port-85 doesn't have on its homepage.
// Rather than delete this shared component (still needed on Registration/Forgot Password/other
// portals' pre-login pages, none of which have their own sidebar logo), it now renders nothing when
// the current route is the homepage specifically, matching how AccessibilityToolbar.jsx already
// suppresses itself there. NewLogin.jsx (rendered inside LandingPage_new_one.jsx's sidebar) is the
// sole visible branding element on the homepage now, exactly mirroring port-85's structure.
function isHomePath(pathname) {
    return pathname === '/';
}

// Phase C.7.4B (§4/§5): Registration's logo/title need substantially more visual presence than the
// compact default this header uses everywhere else (staff pre-login pages, Forgot/Reset/Set
// Password) — those pages should not change. Scoped via this route check + a conditional class
// rather than changing `.ent-public-header`'s own default size, so every other consumer is
// unaffected; the homepage is unaffected regardless since it never renders this component at all.
// Phase C.7.4C (§3/§11.2) had extended this emphasis to the Registration Success page too. Phase
// C.7.4D (live design review) reversed that specific decision: RegistrationMessage.jsx's own card
// already re-displays the logo/org-name prominently (large, centered) directly above the "Thank
// you"/Application ID content, making this top bar redundant duplication on that one page
// specifically — not a rendering defect, a content-redundancy call. `/registrationComplete` now
// suppresses this header entirely (isHomePath-style `return null`, see below) instead of emphasizing
// it; `/registration` keeps the emphasized treatment, since Form 1 has no other on-page branding.
function isRegistrationPath(pathname) {
    return pathname === '/registration';
}
function isRegistrationCompletePath(pathname) {
    return pathname === '/registrationComplete';
}

// Admin Login/Home Page UX Refinement phase (Step 1 of the Admin-side sequence — scoped to
// /admin/login only, per that phase's own explicit "do not touch other pages yet" instruction):
// this shared header renders the candidate-facing "National Bank PO Recruitment 2026" recruitment
// banner, which reads as a recruitment application page header rather than a focused enterprise
// Admin Login screen — the requesting brief explicitly calls for its removal from this one page.
// BankAdmin/HelpDesk/Biometric logins (which share this same PublicHeader/PublicLayout mount point)
// are deliberately left unchanged — same "one page at a time" scope discipline already established
// for isHomePath/isRegistrationCompletePath above.
function isAdminLoginPath(pathname) {
    return pathname === '/admin/login';
}

// Phase C.7.4D-R1 (§6/§10 Candidate Portal Header/Footer Theme Integrity): a live screenshot flagged
// New Registration's header as still using the old plain-white `.ent-public-header` fill while the
// homepage hero, PrivateHeader (authenticated area), and both footers already share one red→teal
// gradient (`linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%)`,
// established C.7.4C §15). Forgot Password shares the same complaint (§2 "legacy...visual language
// inconsistent with the newer Candidate Portal"). Scoped to just these 2 candidate-facing routes via
// a dedicated class — NOT applied to `.ent-public-header` itself — because this component is also
// shared with every staff (Admin/BankAdmin/HelpDesk/Biometric) pre-login screen, which every prior
// phase has explicitly left untouched; this keeps that freeze intact while unifying only the
// candidate journey. Reset/Set Password intentionally not included here — they still render the
// older `.login-form-wrap` shell entirely (not just this header), out of this phase's named scope;
// disclosed as a known follow-up in the phase report rather than silently expanded into.
function isCandidatePublicJourneyPath(pathname) {
    return pathname === '/registration' || pathname === '/forgot-password';
}

function PublicHeader() {
    // Unused value — its setter is what forces the re-render described above once branding resolves.
    const [, setBrandingVersion] = useState(0);
    // Phase C.7.4A (§21 root-cause fix): see the componentDidMount comment below for what this closes.
    const [portalLogo, setPortalLogo] = useState(() => localStorage['Portal_Logo'] || '');

    useEffect(() => {
        _branding.applyBranding().then(() => setBrandingVersion(v => v + 1));
    }, []);

    // Phase C.7.4A (§21 "Why Does the Registration Page Still Look Legacy?" root-cause fix): proven
    // via live inspection — on Registration_new.jsx (and every other PublicHeader consumer that
    // populates Portal_Logo itself, e.g. ForgotPassword_new.jsx), the page's own
    // _authService.cachePortalConfiguration() call is async, and PublicHeader's old code read
    // localStorage['Portal_Logo'] synchronously at render time with no way to know once that call
    // resolved — its only re-render trigger was applyBranding() (an unrelated EnterpriseConfiguration
    // fetch), so whichever of the two async calls happened to finish first decided whether the real
    // tenant logo ever appeared. Confirmed live: localStorage['Portal_Logo'] held the correct value
    // seconds after page load, but the already-rendered <img> kept showing the resolveLogoUrl()
    // fallback (EnterpriseConfiguration Branding.LogoUrl, "../parbhani.jpg") forever, because nothing
    // ever told this component to look again. This second effect makes PublicHeader independently
    // responsible for its own fresh value — same existing, already-idempotent, anonymous endpoint
    // every other consumer already calls (AuthService.cachePortalConfiguration()), not a new API —
    // and updates state once it resolves, exactly mirroring the homepage's own working pattern
    // (LandingPage_new_one.jsx's resData state, populated the same way, from the same endpoint).
    useEffect(() => {
        _authService.cachePortalConfiguration().then(() => {
            if (localStorage['Portal_Logo']) {
                setPortalLogo(localStorage['Portal_Logo']);
            }
        });
    }, []);

    if (typeof window !== 'undefined' &&
        (isHomePath(window.location.pathname) || isRegistrationCompletePath(window.location.pathname) || isAdminLoginPath(window.location.pathname))) {
        return null;
    }

    // Phase C.7.2 (Branding Foundation): resolved through the single authoritative
    // _branding.resolveLogoUrl() priority chain (Portal_Logo → branding LogoUrl → PROJECT_LOGO) —
    // same priority C.6.2.1 established for the homepage. `portalLogo` (state, kept fresh by the
    // effect above) replaces the old direct localStorage['Portal_Logo'] read — see that effect's
    // comment for why the direct read was the actual root cause of the stale-logo defect.
    const logoUrl = _branding.resolveLogoUrl(portalLogo, PROJECT_LOGO);
    const orgName = _branding.resolveOrganizationName("Enterprise Registration Platform");
    const isRegistration = typeof window !== 'undefined' && isRegistrationPath(window.location.pathname);
    const isCandidateJourney = typeof window !== 'undefined' && isCandidatePublicJourneyPath(window.location.pathname);

    return (
        <div className={"ent-public-header" + (isRegistration ? " ent-public-header-emphasized" : "") + (isCandidateJourney ? " ent-public-header-candidate" : "")}>
            <div className="ent-public-header-brand">
                <img src={logoUrl} alt={orgName} />
                <h1 className="ent-public-header-org-name">{orgName}</h1>
            </div>
        </div>
    );
}

export default PublicHeader;
